import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Area, AreaChart, ReferenceLine, ComposedChart
} from 'recharts';
import './App.css';

// Get backend URL from environment
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
console.log('API_BASE_URL configured as:', API_BASE_URL);
console.log('process.env.REACT_APP_BACKEND_URL:', process.env.REACT_APP_BACKEND_URL);

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const App = () => {
  const [user, setUser] = useState(null);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [currentView, setCurrentView] = useState('dashboard');
  const [selectedPeriod, setSelectedPeriod] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    viewType: 'monthly' // 'monthly' or 'yearly'
  });
  const [comparePeriod, setComparePeriod] = useState({
    year: new Date().getFullYear() - 1,
    month: new Date().getMonth() + 1,
    viewType: 'monthly'
  });
  const [showComparison, setShowComparison] = useState(false);
  const [compareData, setCompareData] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [renderKey, setRenderKey] = useState(0);
  const [dashboardData, setDashboardData] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState('TODOS');
  const [lastUpdated, setLastUpdated] = useState(null);

  // Loading and error states
  const [toast, setToast] = useState(null);
  const [presupuestosDetalle, setPresupuestosDetalle] = useState(null);
  const [facturacionAnual, setFacturacionAnual] = useState(null);

  // Fetch presupuestos detailed data
  const fetchPresupuestosDetalle = async () => {
    if (!user) return;

    try {
      // Build params conditionally - only add month for monthly view
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/presupuestos/detalle?year=${selectedPeriod.year}&user_param=${user.username}`;
      if (selectedPeriod.viewType === 'monthly') {
        url += `&month=${selectedPeriod.month}`;
      }
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error('Error al obtener datos detallados de presupuestos');
      }

      const data = await response.json();
      setPresupuestosDetalle(data);
    } catch (error) {
      console.error('Error fetching presupuestos detalle:', error);
      showToast('Error al cargar detalle de presupuestos', 'error');
    }
  };

  // Fetch annual facturación data for dashboard chart
  const fetchFacturacionAnual = async () => {
    if (!user) return;

    try {
      console.log('Fetching facturacion anual for year:', selectedPeriod.year);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/facturacion/anual?year=${selectedPeriod.year}&user_param=${user.username}`;
      console.log('Facturacion anual URL:', url);
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error('Error al obtener datos anuales de facturación');
      }

      const data = await response.json();
      console.log('Facturacion anual data received:', data);
      setFacturacionAnual(data);
    } catch (error) {
      console.error('Error fetching facturacion anual:', error);
      showToast('Error al cargar datos anuales', 'error');
    }
  };

  // Effect to fetch presupuestos detail when view changes
  useEffect(() => {
    if (currentView === 'presupuestos-detail' && user) {
      fetchPresupuestosDetalle();
    }
  }, [currentView, selectedPeriod.year, selectedPeriod.month, user]);

  // Effect to fetch annual facturación data - always load for dashboard chart
  useEffect(() => {
    if (user) {
      fetchFacturacionAnual();
    }
  }, [selectedPeriod.year, user]);

  // Show toast notification
  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Skeleton loading component
  const renderSkeleton = () => (
    <div className="dashboard-skeleton">
      <div className="skeleton-header">
        <div className="skeleton-title"></div>
        <div className="skeleton-badge"></div>
      </div>
      <div className="dashboard-grid">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="col-span-4">
            <div className="skeleton-card">
              <div className="skeleton-chart"></div>
              <div className="skeleton-text"></div>
              <div className="skeleton-text-small"></div>
            </div>
          </div>
        ))}
        <div className="col-span-8">
          <div className="skeleton-card">
            <div className="skeleton-chart-wide"></div>
          </div>
        </div>
        <div className="col-span-4">
          <div className="skeleton-card">
            <div className="skeleton-chart"></div>
          </div>
        </div>
      </div>
    </div>
  );

  // Empty state component
  const renderEmptyState = (message = 'No hay datos disponibles') => (
    <div className="empty-state">
      <div className="empty-icon">📊</div>
      <h3>Sin datos</h3>
      <p>{message}</p>
      <button onClick={refreshData} className="retry-btn">
        ↻ Intentar de nuevo
      </button>
    </div>
  );

  // Toast notification component
  const renderToast = () => {
    if (!toast) return null;
    
    return (
      <div className={`toast toast-${toast.type}`}>
        <div className="toast-content">
          <span className="toast-icon">
            {toast.type === 'success' ? '✅' : toast.type === 'error' ? '❌' : 'ℹ️'}
          </span>
          <span className="toast-message">{toast.message}</span>
        </div>
        <button 
          className="toast-close" 
          onClick={() => setToast(null)}
        >
          ×
        </button>
      </div>
    );
  };

  const doctorFilters = [
    'TODOS',
    'Dr. Javier Echarri',
    'Dra. Ariadna Tombas', 
    'Dr. Manuel Cabezas',
    'Dr. Victor Orozco',
    'Dra. Marina de la Puente',
    'Dr. Xavier Ribas',
    'Dr. Pablo Carmona'
  ];

  // User roles for UI access control
  const UserRole = {
    ADMIN: 'Admin',
    AUXILIAR: 'Auxiliar',
    DIRECTOR: 'Director'
  };

  const hasAccess = (requiredRoles) => {
    if (!user) return false;
    return requiredRoles.includes(user.role);
  };

  const login = async () => {
    console.log('=== LOGIN FUNCTION STARTED ===');
    console.log('Login data:', loginData);
    
    if (!loginData.username || !loginData.password) {
      console.log('Missing credentials');
      setError('Por favor ingrese usuario y contraseña');
      return;
    }

    try {
      setLoginLoading(true);
      setError(null);
      
      const loginUrl = `${API_BASE_URL}/api/auth/login?username=${loginData.username}&password=${loginData.password}`;
      console.log('Making login request to:', loginUrl);
      
      const response = await axios.post(loginUrl);
      console.log('Login response received:', response.data);
      
      if (response.data && response.data.user) {
        console.log('Setting user:', response.data.user);
        setUser(response.data.user);
        console.log('=== LOGIN SUCCESS - USER SET ===');
        // Note: fetchAvailableYears will be called by useEffect when user is set
      } else {
        throw new Error('Invalid response from login');
      }
      
    } catch (err) {
      console.error('=== LOGIN ERROR ===', err);
      setError(err.response?.data?.detail || 'Login failed');
      setUser(null);
    } finally {
      console.log('Setting login loading to false');
      setLoginLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setDashboardData(null);
    setAvailableYears([]);
    setCurrentView('dashboard');
    setError(null);
  };

  const fetchAvailableYears = async (userIdentifier) => {
    try {
      console.log('Fetching available years for user:', userIdentifier);
      const response = await axios.get(`${API_BASE_URL}/api/available-years`, {
        params: { user_param: userIdentifier },
        timeout: 10000
      });
      
      const years = response.data.years || [];
      console.log('Available years fetched:', years);
      setAvailableYears(years);
      
      // Set default year to the most recent available year
      if (years.length > 0 && (!selectedPeriod.year || !years.includes(selectedPeriod.year))) {
        setSelectedPeriod(prev => ({
          ...prev,
          year: years[0] // Most recent year (already sorted descending)
        }));
      }
    } catch (err) {
      console.error('Error fetching available years:', err);
      // Don't throw error to prevent interfering with login flow
      // Fallback to current year if error
      const currentYear = new Date().getFullYear();
      setAvailableYears([currentYear, currentYear - 1, currentYear - 2]);
    }
  };

  const fetchDashboardData = async () => {
    return fetchDashboardDataForUser(user);
  };

  const fetchDashboardDataForUser = async (userObj) => {
    const targetUser = userObj || user;
    if (!targetUser) {
      console.log('fetchDashboardDataForUser: No user provided');
      return;
    }
    
    try {
      console.log('fetchDashboardDataForUser: Starting data fetch for user', targetUser.username);
      setDashboardLoading(true);

      const params = {
        year: selectedPeriod.year,
        user_param: targetUser.username
      };
      
      // Only include month parameter if in monthly view
      if (selectedPeriod.viewType === 'monthly') {
        params.month = selectedPeriod.month;
      }
      
      console.log('fetchDashboardDataForUser: API request params', params);
      console.log('fetchDashboardDataForUser: Making request to', `${API_BASE_URL}/api/dashboard/kpis`);
      
      const response = await axios.get(`${API_BASE_URL}/api/dashboard/kpis`, {
        params: params,
        timeout: 60000 // 60 seconds timeout
      });
      
      console.log('fetchDashboardDataForUser: API response received successfully!');
      console.log('fetchDashboardDataForUser: Response data keys:', Object.keys(response.data));
      console.log('fetchDashboardDataForUser: box_rendimiento present:', !!response.data.box_rendimiento);
      console.log('fetchDashboardDataForUser: box_objetivos present:', !!response.data.box_objetivos);
      setDashboardData(response.data);
      setError(null);
      
      // Force re-render after data is set
      setRenderKey(prev => prev + 1);
      console.log('fetchDashboardDataForUser: Forced re-render triggered');
      
      console.log('fetchDashboardDataForUser: Dashboard data set successfully');
      console.log('fetchDashboardDataForUser: Setting dashboardData with box data:', {
        box_rendimiento: !!response.data.box_rendimiento,
        box_objetivos: !!response.data.box_objetivos
      });
    } catch (err) {
      console.error('fetchDashboardDataForUser: Error occurred', err);
      // Don't set error here as it might interfere with login state
      // setError(err.response?.data?.detail || 'Failed to fetch data');
      console.error('Dashboard fetch failed but keeping user logged in');
    } finally {
      console.log('fetchDashboardDataForUser: Setting dashboard loading to false');
      setDashboardLoading(false);
    }
  };

  const refreshData = async () => {
    if (!user) return;
    
    setDashboardLoading(true);
    setError(null);
    
    try {
      await axios.post(`${API_BASE_URL}/api/refresh?user_param=${user.username}`);
      
      // Refresh available years and dashboard data
      await fetchAvailableYears(user.username);
      await fetchDashboardData();
      setLastUpdated(new Date());
      showToast('Datos actualizados correctamente', 'success');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Error al refrescar datos';
      setError(errorMsg);
      showToast('Error al refrescar los datos', 'error');
    } finally {
      setDashboardLoading(false);
    }
  };

  const fetchCompareData = async () => {
    if (!user || !showComparison) return;
    
    try {
      setDashboardLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/dashboard/kpis`, {
        params: {
          year: comparePeriod.year,
          month: comparePeriod.month,
          user_param: user.username
        }
      });
      setCompareData(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch compare data');
    } finally {
      setDashboardLoading(false);
    }
  };

  useEffect(() => {
    if (user && showComparison) {
      fetchCompareData();
    }
  }, [comparePeriod, user, showComparison]);

  // Effect to initialize user data after login
  useEffect(() => {
    if (user && user.username) {
      console.log('User logged in, fetching available years...');
      fetchAvailableYears(user.username).catch((error) => {
        console.error('Error fetching available years after login:', error);
        // Don't reset user state even if this fails
      });
    }
  }, [user]);

  useEffect(() => {
    console.log('Dashboard useEffect triggered - user:', user, 'selectedPeriod:', selectedPeriod);
    if (user) {
      console.log('User exists, calling fetchDashboardData...');
      fetchDashboardData();
    } else {
      console.log('No user, skipping dashboard fetch');
    }
  }, [selectedPeriod, user]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${value}%`;
  };

  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const renderLoginScreen = () => (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>🦷 ClinicGPS</h1>
          <p>by Dr. Javier Echarri</p>
        </div>
        
        <div className="login-form">
          <div className="form-group">
            <label>Usuario:</label>
            <input
              type="text"
              value={loginData.username}
              onChange={(e) => setLoginData(prev => ({...prev, username: e.target.value}))}
              className="login-input"
              placeholder="Ingrese su usuario"
            />
          </div>
          
          <div className="form-group">
            <label>Contraseña:</label>
            <input
              type="password"
              value={loginData.password}
              onChange={(e) => setLoginData(prev => ({...prev, password: e.target.value}))}
              className="login-input"
              placeholder="Ingrese su contraseña"
            />
          </div>
          
          <button 
            onClick={login} 
            disabled={loginLoading}
            className="login-button"
          >
            {loginLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </button>
        </div>
        
        {error && (
          <div className="error-message">
            ⚠️ {error}
          </div>
        )}
      </div>
    </div>
  );

  const renderHeader = () => (
    <header className="dashboard-header sticky top-0 z-10"> 
      <div className="header-container">
        <div className="header-left">
          <h1>🦷 ClinicGPS</h1>
        </div>
        
        <div className="header-center">
          <div className="period-controls">
            <div className="period-selector">
              <select 
                value={selectedPeriod.viewType} 
                onChange={(e) => setSelectedPeriod(prev => ({...prev, viewType: e.target.value}))}
                className="filter-select"
              >
                <option value="monthly">Mensual</option>
                <option value="yearly">Anual</option>
              </select>
              
              <select 
                value={selectedPeriod.year} 
                onChange={(e) => setSelectedPeriod(prev => ({...prev, year: parseInt(e.target.value)}))}
                className="filter-select"
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
                {/* Fallback if no years loaded yet */}
                {availableYears.length === 0 && (
                  <>
                    <option value={2025}>2025</option>
                    <option value={2024}>2024</option>
                    <option value={2023}>2023</option>
                  </>
                )}
              </select>
              
              {selectedPeriod.viewType === 'monthly' && (
                <select 
                  value={selectedPeriod.month} 
                  onChange={(e) => setSelectedPeriod(prev => ({...prev, month: parseInt(e.target.value)}))}
                  className="filter-select"
                >
                  {months.map((month, idx) => (
                    <option key={idx} value={idx + 1}>{month}</option>
                  ))}
                </select>
              )}
            </div>

            {showComparison && (
              <div className="comparison-selector">
                <span className="comparison-label">Comparar con:</span>
                <select 
                  value={comparePeriod.viewType} 
                  onChange={(e) => setComparePeriod(prev => ({...prev, viewType: e.target.value}))}
                  className="filter-select small"
                >
                  <option value="monthly">Mensual</option>
                  <option value="yearly">Anual</option>
                </select>
                
                <select 
                  value={comparePeriod.year} 
                  onChange={(e) => setComparePeriod(prev => ({...prev, year: parseInt(e.target.value)}))}
                  className="filter-select small"
                >
                  {availableYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                  {availableYears.length === 0 && (
                    <>
                      <option value={2024}>2024</option>
                      <option value={2023}>2023</option>
                    </>
                  )}
                </select>
                
                {comparePeriod.viewType === 'monthly' && (
                  <select 
                    value={comparePeriod.month} 
                    onChange={(e) => setComparePeriod(prev => ({...prev, month: parseInt(e.target.value)}))}
                    className="filter-select small"
                  >
                    {months.map((month, idx) => (
                      <option key={idx} value={idx + 1}>{month}</option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>
        </div>
        
        <div className="header-right">
          <button
            onClick={() => {
              setShowComparison(!showComparison);
              if (!showComparison) {
                fetchCompareData();
              }
            }}
            className={showComparison ? 'action-btn active' : 'action-btn'}
          >
            📊 {showComparison ? 'Ocultar Comparación' : 'Comparar'}
          </button>
          
          <button 
            onClick={refreshData}
            disabled={dashboardLoading}
            className="refresh-btn"
            title="Refrescar datos"
          >
            ↻
          </button>
          
          {lastUpdated && (
            <div className="last-updated">
              Última actualización: {lastUpdated.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
            </div>
          )}
          
          <div className="user-info">
            <span>{user.name} ({user.role})</span>
            <button onClick={logout} className="logout-btn">Salir</button>
          </div>
        </div>
      </div>
    </header>
  );

  const renderNavigation = () => (
    <nav className="main-nav">
      <button 
        className={currentView === 'dashboard' ? 'nav-button active' : 'nav-button'}
        onClick={() => setCurrentView('dashboard')}
      >
        📊 Dashboard
      </button>
    </nav>
  );

  const renderKPICard = ({ title, value, compareValue, subtitle, color = '#3498db', icon }) => (
    <div className="kpi-card" style={{ borderLeft: `4px solid ${color}` }}>
      <div className="kpi-header">
        <span className="kpi-icon">{icon}</span>
        <h3>{title}</h3>
      </div>
      <div className="kpi-value">{value}</div>
      {showComparison && compareValue !== undefined && (
        <div className="kpi-comparison">
          <div className="compare-value">
            Comparar: {compareValue}
          </div>
          {compareValue !== null && value !== null && (
            <div className="compare-diff">
              {(() => {
                const currentNum = parseFloat(String(value).replace(/[€,%]/g, ''));
                const compareNum = parseFloat(String(compareValue).replace(/[€,%]/g, ''));
                if (!isNaN(currentNum) && !isNaN(compareNum)) {
                  const diff = currentNum - compareNum;
                  const diffPercent = compareNum !== 0 ? ((diff / compareNum) * 100).toFixed(1) : '∞';
                  const arrow = diff > 0 ? '↗️' : diff < 0 ? '↘️' : '➡️';
                  const diffColor = diff > 0 ? '#2ecc71' : diff < 0 ? '#e74c3c' : '#95a5a6';
                  return (
                    <span style={{ color: diffColor }}>
                      {arrow} {diffPercent}% ({diff > 0 ? '+' : ''}{diff.toFixed(2)})
                    </span>
                  );
                }
                return null;
              })()}
            </div>
          )}
        </div>
      )}
      {subtitle && <div className="kpi-subtitle">{subtitle}</div>}
    </div>
  );

  const renderDashboard = () => {
    // Show skeleton while loading
    if (dashboardLoading && !dashboardData) {
      return renderSkeleton();
    }

    // Show dashboard with empty data if no data loaded yet
    if (!dashboardData) {
      console.log('Rendering dashboard with no data - probably still loading...');
      // Create empty data structure to allow dashboard to render
      const emptyData = {
        financial: null,
        presupuestos: null,
        recalls: null,
        produccion: null,
        facturacion: null,
        gastos_categorizados: null,
        box_ocupacion: null,
        box_rendimiento: null,
        box_objetivos: null
      };
      // Continue with dashboard render using empty data
      return renderDashboardWithData(emptyData);
    }

    return renderDashboardWithData(dashboardData);
  };

  const renderDashboardWithData = (data) => {
    const { 
      financial = {
        ingresos_totales: 0,
        gastos_totales: 0,
        margen: 0,
        margen_porcentaje: 0,
        objetivo_ingresos: 0,
        cumplimiento_objetivo: 0
      }, 
      presupuestos = {
        emitidos: 0,
        aceptados: 0,
        rechazados: 0,
        en_seguimiento: 0,
        tasa_conversion: 0,
        ticket_medio: 0,
        ticket_medio_aceptado: 0,
        total_aceptados: 0
      }, 
      recalls = {
        completados: 0,
        objetivo_mes: 0,
        pct_completados: 0
      }, 
      produccion = {
        top_5_doctores: []
      }, 
      facturacion = {
        facturacion_emitida: 0,
        facturacion_cobrada: 0,
        objetivo_facturacion: 0,
        cumplimiento_objetivo: 0
      }, 
      gastos_categorizados = null, 
      box_ocupacion = null, 
      box_rendimiento = null, 
      box_objetivos = null
    } = data || {};
    
    // Get comparison data if available
    const compareFinancial = compareData?.financial;
    const comparePresupuestos = compareData?.presupuestos;
    const compareRecalls = compareData?.recalls;
    
    // Check if using demo data
    const isDemoData = financial?.demo_data || presupuestos?.demo_data || 
                      recalls?.demo_data || produccion?.demo_data ||
                      facturacion?.demo_data || gastos_categorizados?.demo_data || 
                      box_ocupacion?.demo_data ||
                      box_rendimiento?.demo_data ||
                      box_objetivos?.demo_data;

    // Prepare chart data for circular charts
    const financialPieData = [
      { name: 'Ingresos', value: financial?.ingresos_totales || 0, fill: '#10b981' },
      { name: 'Gastos', value: financial?.gastos_totales || 0, fill: '#ef4444' }
    ];

    const presupuestosPieData = [
      { name: 'Aceptados', value: presupuestos?.aceptados || 0, fill: '#2ecc71' },
      { name: 'Rechazados', value: presupuestos?.rechazados || 0, fill: '#e74c3c' },
      { name: 'En Seguimiento', value: presupuestos?.en_seguimiento || 0, fill: '#f39c12' }
    ];

    const recallsPieData = [
      { name: 'Completados', value: recalls?.completados || 0, fill: '#2ecc71' },
      { name: 'Agendados', value: recalls?.agendados || 0, fill: '#f39c12' },
      { name: 'Contactados', value: Math.max(0, (recalls?.contactados || 0) - (recalls?.agendados || 0)), fill: '#3498db' },
      { name: 'Pendientes', value: Math.max(0, (recalls?.objetivo_mes || 0) - (recalls?.contactados || 0)), fill: '#95a5a6' }
    ];

    // Calculate percentages for objectives
    const financialMarginPercent = financial?.margen_porcentaje || 
      (financial?.ingresos_totales > 0 ? ((financial?.margen / financial.ingresos_totales) * 100) : 0);
    
    const presupuestosConversionPercent = presupuestos?.tasa_conversion || 0;
    
    const recallsCompletionPercent = (recalls?.objetivo_mes || 0) > 0 ? 
      ((recalls?.completados || 0) / (recalls?.objetivo_mes || 0)) * 100 : 0;

    return (
      <div className="dashboard">
        {isDemoData && (
          <div className="demo-data-banner">
            🚧 <strong>Datos de Demostración</strong> - Para usar datos reales, configure el ID del Google Sheet en las variables de entorno
          </div>
        )}
        
        {showComparison && compareData && (
          <div className="comparison-info">
            <h3>📊 Comparando: {selectedPeriod.viewType === 'monthly' ? months[selectedPeriod.month - 1] : ''} {selectedPeriod.year} vs {comparePeriod.viewType === 'monthly' ? months[comparePeriod.month - 1] : ''} {comparePeriod.year}</h3>
          </div>
        )}
        
        <div className="page-header">
          <h1>🦷 Dashboard ClinicGPS</h1>
          <div className="period-indicator">
            {selectedPeriod.viewType === 'yearly' ? (
              <span className="period-badge annual">📊 Vista Anual {selectedPeriod.year}</span>
            ) : (
              <span className="period-badge monthly">📅 {months[selectedPeriod.month - 1]} {selectedPeriod.year}</span>
            )}
          </div>
        </div>

        <div className="dashboard-overview">
          <h2>📊 Vista General del Negocio</h2>
          <div className="dashboard-grid">
            
            {/* KPIs Section - 4x3 columns = 12 col */}
            <div className="kpi-section col-span-4">
              <div className="section-header">
                <h3>💰 Indicadores Financieros</h3>
                <div className={`objective-badge ${financialMarginPercent >= 0 ? 'success' : 'danger'}`}>
                  {financialMarginPercent.toFixed(2)}% Margen
                </div>
              </div>
              <div className="kpi-cards-grid">
                {/* Financial KPI Card */}
                <div className="overview-card-small" onClick={() => setCurrentView('financial-detail')}>
                  <div className="chart-container-tiny">
                    <ResponsiveContainer width="100%" height={120}>
                      <PieChart>
                        <Pie
                          data={financialPieData}
                          cx="50%"
                          cy="50%"
                          outerRadius={40}
                          fill="#6366f1"
                          dataKey="value"
                        >
                          {financialPieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="kpi-summary">
                    <div className="kpi-title">Ingresos vs Gastos</div>
                    <div className="kpi-value">{formatCurrency(financial?.ingresos_totales || 0)}</div>
                    <div className="kpi-subtitle">{financial?.cumplimiento_objetivo?.toFixed(0)}% del objetivo</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="kpi-section col-span-4">
              <div className="section-header">
                <h3>📋 Presupuestos</h3>
                <div className="objective-badge primary">
                  {presupuestosConversionPercent.toFixed(0)}% Conversión
                </div>
              </div>
              <div className="kpi-cards-grid">
                {/* Presupuestos Funnel Chart */}
                <div className="overview-card-small" onClick={() => setCurrentView('presupuestos-detail')}>
                  <div className="chart-container-tiny">
                    <div className="funnel-container">
                      <div className="funnel-stage">
                        <div 
                          className="funnel-bar funnel-stage-1"
                          style={{ 
                            width: '100%',
                            background: 'linear-gradient(135deg, #6366f1, #4f46e5)'
                          }}
                        >
                          <span className="funnel-label">Emitidos<br/>{presupuestos?.emitidos || 0}</span>
                        </div>
                      </div>
                      <div className="funnel-connector"></div>
                      <div className="funnel-stage">
                        <div 
                          className="funnel-bar funnel-stage-2"
                          style={{ 
                            width: (presupuestos?.emitidos || 0) > 0 ? `${((presupuestos?.aceptados || 0) / (presupuestos?.emitidos || 1)) * 80 + 20}%` : '20%',
                            background: 'linear-gradient(135deg, #10b981, #059669)'
                          }}
                        >
                          <span className="funnel-label">Aceptados<br/>{presupuestos?.aceptados || 0}</span>
                        </div>
                      </div>
                      {(presupuestos?.en_seguimiento || 0) > 0 && (
                        <>
                          <div className="funnel-connector-side"></div>
                          <div className="funnel-stage-side">
                            <div 
                              className="funnel-bar-side"
                              style={{ 
                                width: (presupuestos?.emitidos || 0) > 0 ? `${((presupuestos?.en_seguimiento || 0) / (presupuestos?.emitidos || 1)) * 60 + 15}%` : '15%',
                                background: 'linear-gradient(135deg, #f59e0b, #d97706)'
                              }}
                            >
                              <span className="funnel-label-side">Seguimiento: {presupuestos?.en_seguimiento || 0}</span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="kpi-summary">
                    <div className="kpi-title">Conversión</div>
                    <div className="kpi-value">{presupuestos?.aceptados || 0}/{presupuestos?.emitidos || 0}</div>
                    <div className="kpi-subtitle">Total Aceptados</div>
                    <div className="kpi-value-small">{presupuestos?.total_aceptados || presupuestos?.aceptados || 0}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="kpi-section col-span-4">
              <div className="section-header">
                <h3>📞 Recalls</h3>
                <div className="objective-badge warning">
                  {recallsCompletionPercent.toFixed(0)}% Completado
                </div>
              </div>
              <div className="kpi-cards-grid">
                {/* Recalls Progress Card */}
                <div className="overview-card-small" onClick={() => setCurrentView('recalls-detail')}>
                  <div className="progress-container-mini">
                    <div className="progress-bar-container-mini">
                      <div 
                        className="progress-bar-fill-mini"
                        style={{ 
                          width: `${Math.min(100, recallsCompletionPercent)}%`,
                          backgroundColor: recallsCompletionPercent >= 100 ? '#10b981' : recallsCompletionPercent >= 75 ? '#f59e0b' : '#ef4444'
                        }}
                      />
                    </div>
                    <div className="kpi-summary">
                      <div className="kpi-title">Progreso</div>
                      <div className="kpi-value">{recalls?.completados || 0}/{recalls?.objetivo_mes || 0}</div>
                      <div className="kpi-subtitle">Faltan: {Math.max(0, (recalls?.objetivo_mes || 0) - (recalls?.completados || 0))}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Production Chart - 6 columns */}
            {hasAccess([UserRole.ADMIN, UserRole.DIRECTOR]) && produccion && produccion.top_5_doctores && (
              <div className="col-span-6">
                <div className="section-card">
                  <div className="section-header">
                    <h3>👨‍⚕️ Producción por Doctores</h3>
                    <div className="objective-badge neutral">Top 5</div>
                  </div>
                  <div className="chart-container-wide">
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={(Array.isArray(produccion.top_5_doctores) ? produccion.top_5_doctores.slice(0, 5) : [])} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis 
                          dataKey="doctor" 
                          fontSize={11} 
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          stroke="#64748b"
                        />
                        <YAxis fontSize={11} stroke="#64748b" />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Bar dataKey="produccion" fill="#6366f1" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {/* Facturación Annual Chart - Always show bar chart with monthly data */}
            {hasAccess([UserRole.ADMIN, UserRole.DIRECTOR]) && (
              <div className="col-span-6">
                <div className="section-card">
                  <div className="section-header">
                    <h3>💰 Facturación Anual {selectedPeriod.year}</h3>
                    <div className="objective-badge primary">
                      Real vs Objetivo por Mes
                    </div>
                  </div>
                  {facturacionAnual?.facturacion_mensual ? (
                    <>
                      <div className="chart-container">
                        <ResponsiveContainer width="100%" height={280}>
                          <ComposedChart 
                            data={facturacionAnual.facturacion_mensual?.map(item => ({
                              mes: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'][item.mes - 1],
                              facturacion: item.facturacion,
                              objetivo: item.objetivo,
                              cumplimiento: item.facturacion >= item.objetivo
                            }))}
                            margin={{ top: 20, right: 30, left: 40, bottom: 20 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="mes" fontSize={11} stroke="#64748b" />
                            <YAxis fontSize={11} stroke="#64748b" />
                            <Tooltip 
                              formatter={(value, name) => [
                                formatCurrency(value), 
                                name === 'facturacion' ? 'Facturación Real' : 'Objetivo Mensual'
                              ]}
                              labelStyle={{ color: '#374151' }}
                              contentStyle={{ 
                                backgroundColor: '#fff', 
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                              }}
                            />
                            <Legend />
                            <Bar 
                              dataKey="facturacion" 
                              fill="#3498db" 
                              name="Facturación Real" 
                              radius={[4, 4, 0, 0]} 
                            />
                            <Line
                              type="monotone"
                              dataKey="objetivo"
                              stroke="#e74c3c"
                              strokeWidth={3}
                              name="Objetivo"
                              dot={{ fill: '#e74c3c', strokeWidth: 2, r: 4 }}
                              connectNulls={false}
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                      
                      <div className="chart-summary">
                        <div className="summary-stats">
                          <span className="summary-stat">
                            <strong>Total Año:</strong> {formatCurrency(
                              facturacionAnual.total_año || facturacionAnual.facturacion_mensual.reduce((sum, item) => sum + item.facturacion, 0)
                            )}
                          </span>
                          <span className="summary-stat">
                            <strong>Objetivo Año:</strong> {formatCurrency(
                              facturacionAnual.objetivo_año || facturacionAnual.facturacion_mensual.reduce((sum, item) => sum + item.objetivo, 0)
                            )}
                          </span>
                          <span className="summary-stat">
                            <strong>Cumplimiento:</strong> {
                              (() => {
                                const totalAno = facturacionAnual.total_año || facturacionAnual.facturacion_mensual.reduce((sum, item) => sum + item.facturacion, 0);
                                const objetivoAno = facturacionAnual.objetivo_año || facturacionAnual.facturacion_mensual.reduce((sum, item) => sum + item.objetivo, 0);
                                const cumplimiento = objetivoAno > 0 ? ((totalAno / objetivoAno) * 100) : 0;
                                return `${cumplimiento.toFixed(1)}%`;
                              })()
                            }
                          </span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="loading-chart">
                      <div className="loading">Cargando datos anuales de facturación...</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Análisis de Ocupación - 12 columns (full width) */}
            {user && user.role === 'Admin' && box_rendimiento && box_rendimiento.box_rendimiento && Array.isArray(box_rendimiento.box_rendimiento) && box_rendimiento.box_rendimiento.length > 0 && (
              <div className="col-span-12">
                <div className="section-card">
                  <div className="section-header">
                    <h3>📊 Análisis de Ocupación</h3>
                  </div>
                  
                  {console.log('RENDERING BOX ANALYSIS SECTION - Data:', {
                    boxEntries: box_rendimiento?.box_rendimiento?.length || 0
                  })}
                  
                  <div className="space-y-6">
                    {/* BoxDRES Performance Chart - BoxID vs RENDIMIENTO REAL */}
                    {box_rendimiento && box_rendimiento.box_rendimiento && Array.isArray(box_rendimiento.box_rendimiento) && box_rendimiento.box_rendimiento.length > 0 && (
                      <div className="space-y-4">
                        <h4 className="text-sm font-semibold text-gray-700">📊 Rendimiento por BoxID</h4>
                        <div className="text-xs text-gray-500 mb-2">
                          Datos de Google Sheets ClinicGPS → BoxDRES | Y-axis: BoxID | X-axis: RENDIMIENTO REAL (%)
                        </div>
                        <div className="boxes-chart-container">
                          <ResponsiveContainer width="100%" height={400}>
                            <BarChart 
                              data={box_rendimiento.box_rendimiento}
                              layout="horizontal"
                              margin={{ top: 20, right: 30, left: 150, bottom: 20 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                              <XAxis 
                                type="number" 
                                domain={[0, 100]}
                                fontSize={11} 
                                stroke="#64748b"
                                label={{ value: 'RENDIMIENTO REAL (%)', position: 'insideBottom', offset: -10 }}
                              />
                              <YAxis 
                                type="category" 
                                dataKey="box_id" 
                                fontSize={10} 
                                stroke="#64748b" 
                                width={140}
                                interval={0}
                                label={{ value: 'BoxID', angle: -90, position: 'insideLeft' }}
                              />
                              <Tooltip formatter={(value) => [`${value}%`, 'Rendimiento Real']} />
                              <Bar dataKey="rendimiento" fill="#10b981" radius={[0, 4, 4, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Período: {box_rendimiento.periodo}</span>
                          {selectedPeriod.viewType === 'monthly' && (
                            <span className="ml-4">Filtrado por: {months[selectedPeriod.month - 1]} {selectedPeriod.year}</span>
                          )}
                          {selectedPeriod.viewType === 'yearly' && (
                            <span className="ml-4">Filtrado por: Año {selectedPeriod.year}</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Box Objetivos Table (if available) */}
                    {box_objetivos && box_objetivos.objetivos_por_dia && Array.isArray(box_objetivos.objetivos_por_dia) && (
                      <div className="space-y-4">
                        <h4 className="text-sm font-semibold text-gray-700">Objetivos por Día</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-gray-50">
                                <th className="px-3 py-2 text-left font-medium text-gray-700">Día</th>
                                <th className="px-3 py-2 text-center font-medium text-gray-700">Mañana</th>
                                <th className="px-3 py-2 text-center font-medium text-gray-700">Tarde</th>
                              </tr>
                            </thead>
                            <tbody>
                              {box_objetivos.objetivos_por_dia.map((dia, index) => (
                                <tr key={index} className="border-b border-gray-100">
                                  <td className="px-3 py-2 font-medium text-gray-800">{dia.dia}</td>
                                  <td className="px-3 py-2 text-center">
                                    <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                      {dia.objetivo_mañana}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <span className="inline-block px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded-full">
                                      {dia.objetivo_tarde}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderPresupuestosView = () => {
    const [presupuestosData, setPresupuestosData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const fetchData = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/api/presupuestos`, {
            params: { user_param: user.username }
          });
          setPresupuestosData(response.data);
        } catch (err) {
          console.error('Error fetching presupuestos:', err);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }, []);

    if (loading) return <div className="loading">Cargando presupuestos...</div>;

    return (
      <div className="data-view">
        <div className="view-header">
          <h2>📋 Presupuestos</h2>
          <p>Gestión y seguimiento de presupuestos</p>
        </div>
        
        {presupuestosData && presupuestosData.data && presupuestosData.data.length > 0 ? (
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Fecha Emisión</th>
                  <th>Paciente</th>
                  <th>Tratamiento</th>
                  <th>Importe</th>
                  <th>Estado</th>
                  <th>Doctor</th>
                  <th>Fuente</th>
                </tr>
              </thead>
              <tbody>
                {presupuestosData.data.slice(0, 20).map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.FechaEmision}</td>
                    <td>{row.PacienteNombre}</td>
                    <td>{row.Tratamiento}</td>
                    <td>{formatCurrency(parseFloat(row.ImportePresupuestoEUR) || 0)}</td>
                    <td>
                      <span className={`status-badge status-${row.Estado?.toLowerCase().replace(' ', '-')}`}>
                        {row.Estado}
                      </span>
                    </td>
                    <td>{row.DoctorResponsable}</td>
                    <td>{row.FuenteLead}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <p>📋 No hay presupuestos disponibles en este momento</p>
            <p className="text-small">Los datos aparecerán cuando se agreguen al Google Sheets</p>
          </div>
        )}
      </div>
    );
  };

  const renderRecallsView = () => {
    const [recallsData, setRecallsData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const fetchData = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/api/recalls`, {
            params: { user_param: user.username }
          });
          setRecallsData(response.data);
        } catch (err) {
          console.error('Error fetching recalls:', err);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }, []);

    if (loading) return <div className="loading">Cargando recalls...</div>;

    return (
      <div className="data-view">
        <div className="view-header">
          <h2>📞 Recalls</h2>
          <p>Seguimiento de citas y contactos</p>
        </div>
        
        {recallsData && recallsData.data && recallsData.data.length > 0 ? (
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Fecha Prevista</th>
                  <th>Paciente</th>
                  <th>Tipo Recall</th>
                  <th>Estado</th>
                  <th>Doctor</th>
                  <th>Canal Contacto</th>
                  <th>Fecha Estado</th>
                </tr>
              </thead>
              <tbody>
                {recallsData.data.slice(0, 20).map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.FechaPrevista}</td>
                    <td>{row.PacienteNombre}</td>
                    <td>{row.TipoRecall}</td>
                    <td>
                      <span className={`status-badge status-${row.Estado?.toLowerCase().replace(' ', '-')}`}>
                        {row.Estado}
                      </span>
                    </td>
                    <td>{row.DoctorResponsable}</td>
                    <td>{row.CanalContacto}</td>
                    <td>{row.FechaEstado}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <p>📞 No hay recalls disponibles en este momento</p>
            <p className="text-small">Los datos aparecerán cuando se agreguen al Google Sheets</p>
          </div>
        )}
      </div>
    );
  };

  const renderFacturacionView = () => {
    const [facturacionData, setFacturacionData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const fetchData = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/api/facturacion`, {
            params: { user_param: user.username }
          });
          setFacturacionData(response.data);
        } catch (err) {
          console.error('Error fetching facturación:', err);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }, []);

    if (loading) return <div className="loading">Cargando facturación...</div>;

    return (
      <div className="data-view">
        <div className="view-header">
          <h2>💳 Facturación</h2>
          <p>Gestión de facturas y cobros</p>
        </div>
        
        {facturacionData && facturacionData.data && facturacionData.data.length > 0 ? (
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Fecha Factura</th>
                  <th>Número</th>
                  <th>Paciente</th>
                  <th>Doctor</th>
                  <th>Total EUR</th>
                  <th>Estado Cobro</th>
                  <th>Fecha Cobro</th>
                  <th>Cobrado EUR</th>
                </tr>
              </thead>
              <tbody>
                {facturacionData.data.slice(0, 20).map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.FechaFactura}</td>
                    <td>{row.Numero}</td>
                    <td>{row.PacienteNombre}</td>
                    <td>{row.Doctor}</td>
                    <td>{formatCurrency(parseFloat(row.TotalEUR) || 0)}</td>
                    <td>
                      <span className={`status-badge status-${row.EstadoCobro?.toLowerCase().replace(' ', '-')}`}>
                        {row.EstadoCobro}
                      </span>
                    </td>
                    <td>{row.FechaCobro}</td>
                    <td>{formatCurrency(parseFloat(row.CobradoEUR) || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <p>💳 No hay facturas disponibles en este momento</p>
            <p className="text-small">Los datos aparecerán cuando se agreguen al Google Sheets</p>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (dashboardLoading && !dashboardData) {
      return <div className="loading">⏳ Cargando...</div>;
    }

    if (error) {
      return <div className="error">❌ {error}</div>;
    }

    switch (currentView) {
      case 'dashboard':
        return renderDashboard();
      case 'financial-detail':
        return renderFinancialDetail();
      case 'presupuestos-detail':
        return renderPresupuestosDetail();
      case 'recalls-detail': 
        return renderRecallsDetail();
      case 'produccion-detail':
        return renderProduccionDetail();
      case 'facturacion-detail':
        return renderFacturacionDetail();
      case 'box-ocupacion-detail':
        return renderBoxOcupacionDetail();
      case 'presupuestos':
        return renderPresupuestosView();
      case 'recalls':
        return renderRecallsView();
      case 'produccion':
        return <div className="view-placeholder">👨‍⚕️ Vista de Producción (En desarrollo)</div>;
      case 'facturacion':
        return renderFacturacionView();
      case 'box-ocupacion':
        return <div className="view-placeholder">🏢 Vista de Ocupación de Boxes (En desarrollo)</div>;
      default:
        return renderDashboard();
    }
  };

  const renderFinancialDetail = () => {
    if (!dashboardData) {
      return <div className="loading">Cargando datos financieros...</div>;
    }

    const { financial } = dashboardData;
    const compareFinancial = compareData?.financial;

    return (
      <div className="detail-view">
        <div className="detail-header">
          <button className="back-button" onClick={() => setCurrentView('dashboard')}>
            ← Volver al Dashboard
          </button>
          <h2>💰 Detalle de Indicadores Financieros</h2>
        </div>
        
        {showComparison && compareData && (
          <div className="comparison-info">
            <h3>📊 Comparando: {selectedPeriod.viewType === 'monthly' ? months[selectedPeriod.month - 1] : ''} {selectedPeriod.year} vs {comparePeriod.viewType === 'monthly' ? months[comparePeriod.month - 1] : ''} {comparePeriod.year}</h3>
          </div>
        )}

        <div className="kpi-section">
          <h3>💰 Indicadores Financieros</h3>
          <div className="kpi-grid">
            {renderKPICard({
              title: 'Ingresos Totales',
              value: formatCurrency(financial?.ingresos_totales || 0),
              compareValue: showComparison && compareFinancial ? formatCurrency(compareFinancial?.ingresos_totales || 0) : undefined,
              subtitle: `Objetivo: ${formatCurrency(financial?.objetivo_ingresos || 0)}`,
              color: '#2ecc71',
              icon: '💰'
            })}
            
            {renderKPICard({
              title: 'Gastos Totales',
              value: formatCurrency(financial?.gastos_totales || 0),
              compareValue: showComparison && compareFinancial ? formatCurrency(compareFinancial?.gastos_totales || 0) : undefined,
              color: '#e74c3c',
              icon: '💸'
            })}
            
            {renderKPICard({
              title: 'Margen',
              value: formatCurrency(financial?.margen || 0),
              compareValue: showComparison && compareFinancial ? formatCurrency(compareFinancial?.margen || 0) : undefined,
              subtitle: `${financial?.margen_porcentaje?.toFixed(2) || 0}%`,
              color: (financial?.margen || 0) >= 0 ? '#2ecc71' : '#e74c3c',
              icon: '📈'
            })}
            
            {renderKPICard({
              title: 'Cumplimiento Objetivo',
              value: `${financial?.cumplimiento_objetivo?.toFixed(1) || 0}%`,
              compareValue: showComparison && compareFinancial ? `${compareFinancial?.cumplimiento_objetivo?.toFixed(1) || 0}%` : undefined,
              color: (financial?.cumplimiento_objetivo || 0) >= 100 ? '#2ecc71' : '#f39c12',
              icon: '🎯'
            })}
          </div>
          
          {financial && (
            <div className="chart-container">
              <h3>Ingresos vs Gastos</h3>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={[
                  { name: 'Ingresos', value: financial?.ingresos_totales || 0, fill: '#2ecc71' },
                  { name: 'Gastos', value: financial?.gastos_totales || 0, fill: '#e74c3c' }
                ]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="value" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPresupuestosDetail = () => {
    if (!dashboardData) {
      return <div className="loading">Cargando datos de presupuestos...</div>;
    }

    const { presupuestos } = dashboardData;
    const comparePresupuestos = compareData?.presupuestos;

    const presupuestosChartData = [
      { name: 'Aceptados', value: presupuestos?.aceptados || 0, fill: '#10b981' },
      { name: 'Rechazados', value: presupuestos.rechazados, fill: '#ef4444' },
      { name: 'En Seguimiento', value: presupuestos?.en_seguimiento || 0, fill: '#f59e0b' }
    ];

    return (
      <div className="detail-view">
        <div className="detail-header">
          <button className="back-button" onClick={() => setCurrentView('dashboard')}>
            ← Volver al Dashboard
          </button>
          <h2>📋 Detalle de Presupuestos</h2>
        </div>
        
        {showComparison && compareData && (
          <div className="comparison-info">
            <h3>📊 Comparando: {selectedPeriod.viewType === 'monthly' ? months[selectedPeriod.month - 1] : ''} {selectedPeriod.year} vs {comparePeriod.viewType === 'monthly' ? months[comparePeriod.month - 1] : ''} {comparePeriod.year}</h3>
          </div>
        )}

        <div className="detail-grid">
          {/* KPIs Section */}
          <div className="col-span-12">
            <div className="kpi-section">
              <h3>Métricas Generales</h3>
              <div className="kpi-grid">
                {renderKPICard({
                  title: 'Tasa de Conversión',
                  value: formatPercentage(presupuestos.tasa_conversion),
                  compareValue: showComparison && comparePresupuestos ? formatPercentage(comparePresupuestos.tasa_conversion) : undefined,
                  subtitle: `${presupuestos?.aceptados || 0}/${presupuestos?.emitidos || 0}`,
                  color: '#6366f1',
                  icon: '📊'
                })}
                
                {renderKPICard({
                  title: 'Ticket Medio',
                  value: formatCurrency(presupuestos.ticket_medio),
                  compareValue: showComparison && comparePresupuestos ? formatCurrency(comparePresupuestos.ticket_medio) : undefined,
                  subtitle: 'Todos los presupuestos',
                  color: '#6366f1',
                  icon: '📋'
                })}
                
                {renderKPICard({
                  title: 'Ticket Medio Aceptado',
                  value: formatCurrency(presupuestos.ticket_medio_aceptado),
                  compareValue: showComparison && comparePresupuestos ? formatCurrency(comparePresupuestos.ticket_medio_aceptado) : undefined,
                  subtitle: 'Presupuestos aceptados',
                  color: '#10b981',
                  icon: '💎'
                })}
                
                {renderKPICard({
                  title: 'Emitidos',
                  value: presupuestos?.emitidos || 0,
                  compareValue: showComparison && comparePresupuestos ? (comparePresupuestos?.emitidos || 0) : undefined,
                  subtitle: 'En el período',
                  color: '#64748b',
                  icon: '📄'
                })}
              </div>
            </div>
          </div>

          {/* Chart Section */}
          <div className="col-span-6">
            <div className="section-card">
              <h3>Estado de Presupuestos</h3>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={presupuestosChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {presupuestosChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Dropdowns Section */}
          <div className="col-span-6">
            <div className="section-card">
              <h3>Análisis Detallado</h3>
              
              {/* AtPaciente Dropdown */}
              <div className="analysis-dropdown">
                <label className="dropdown-label">📋 Aceptación por Quien Entrega:</label>
                <div className="dropdown-content">
                  {presupuestosDetalle?.por_atpaciente ? (
                    Object.entries(presupuestosDetalle.por_atpaciente).map(([atpaciente, data]) => (
                      <div key={atpaciente} className="dropdown-item">
                        <div className="dropdown-item-header">
                          <span className="dropdown-name">{atpaciente}</span>
                          <span className="dropdown-percentage success">{data.tasa_aceptacion}%</span>
                        </div>
                        <div className="dropdown-item-details">
                          Aceptados: {data.aceptados}/{data.total}
                        </div>
                        <div className="progress-bar-mini">
                          <div 
                            className="progress-fill"
                            style={{ 
                              width: `${data.tasa_aceptacion}%`,
                              backgroundColor: data.tasa_aceptacion >= 70 ? '#10b981' : data.tasa_aceptacion >= 50 ? '#f59e0b' : '#ef4444'
                            }}
                          />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="loading-text">Cargando datos...</div>
                  )}
                </div>
              </div>

              {/* Doctor Dropdown */}
              <div className="analysis-dropdown">
                <label className="dropdown-label">👨‍⚕️ Aceptación por Doctor:</label>
                <div className="dropdown-content">
                  {presupuestosDetalle?.por_doctor ? (
                    Object.entries(presupuestosDetalle.por_doctor).map(([doctor, data]) => (
                      <div key={doctor} className="dropdown-item">
                        <div className="dropdown-item-header">
                          <span className="dropdown-name">{doctor}</span>
                          <span className="dropdown-percentage primary">{data.tasa_aceptacion}%</span>
                        </div>
                        <div className="dropdown-item-details">
                          Aceptados: {data.aceptados}/{data.total}
                        </div>
                        <div className="progress-bar-mini">
                          <div 
                            className="progress-fill"
                            style={{ 
                              width: `${data.tasa_aceptacion}%`,
                              backgroundColor: data.tasa_aceptacion >= 70 ? '#10b981' : data.tasa_aceptacion >= 50 ? '#f59e0b' : '#ef4444'
                            }}
                          />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="loading-text">Cargando datos...</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Referidores Chart - Full Width */}
          <div className="col-span-12">
            <div className="section-card">
              <h3>🔄 Presupuestos por Referidores</h3>
              {presupuestosDetalle?.por_referidor ? (
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart 
                      data={Object.entries(presupuestosDetalle.por_referidor).map(([referidor, data]) => ({
                        referidor,
                        total: data.total,
                        aceptados: data.aceptados,
                        rechazados: data.total - data.aceptados
                      }))}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="referidor" 
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        fontSize={11}
                      />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="aceptados" stackId="a" fill="#10b981" name="Aceptados" />
                      <Bar dataKey="rechazados" stackId="a" fill="#ef4444" name="Rechazados" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="loading-chart">Cargando gráfico de referidores...</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderRecallsDetail = () => {
    if (!dashboardData) {
      return <div className="loading">Cargando datos de recalls...</div>;
    }

    const { recalls } = dashboardData;
    const compareRecalls = compareData?.recalls;

    const recallsChartData = [
      { name: 'Completados', value: recalls?.completados || 0, fill: '#2ecc71' },
      { name: 'Agendados', value: recalls?.agendados || 0, fill: '#f39c12' },
      { name: 'Contactados', value: Math.max(0, (recalls?.contactados || 0) - (recalls?.agendados || 0)), fill: '#3498db' },
      { name: 'Pendientes', value: Math.max(0, (recalls?.objetivo_mes || 0) - (recalls?.contactados || 0)), fill: '#95a5a6' }
    ];

    return (
      <div className="detail-view">
        <div className="detail-header">
          <button className="back-button" onClick={() => setCurrentView('dashboard')}>
            ← Volver al Dashboard
          </button>
          <h2>📞 Detalle de Recalls</h2>
        </div>
        
        {showComparison && compareData && (
          <div className="comparison-info">
            <h3>📊 Comparando: {selectedPeriod.viewType === 'monthly' ? months[selectedPeriod.month - 1] : ''} {selectedPeriod.year} vs {comparePeriod.viewType === 'monthly' ? months[comparePeriod.month - 1] : ''} {comparePeriod.year}</h3>
          </div>
        )}

        <div className="kpi-section">
          <h3>Métricas de Recalls</h3>
          <div className="kpi-grid">
            {renderKPICard({
              title: 'Objetivo del Mes',
              value: recalls?.objetivo_mes || 0,
              compareValue: showComparison && compareRecalls ? (compareRecalls?.objetivo_mes || 0) : undefined,
              color: '#3498db',
              icon: '🎯'
            })}
            
            {renderKPICard({
              title: '% Contactados',
              value: formatPercentage(recalls?.pct_contactados || 0),
              compareValue: showComparison && compareRecalls ? formatPercentage(compareRecalls?.pct_contactados || 0) : undefined,
              subtitle: `${recalls?.contactados || 0}/${recalls?.objetivo_mes || 0}`,
              color: '#f39c12',
              icon: '📞'
            })}
            
            {renderKPICard({
              title: '% Agendados',
              value: formatPercentage(recalls?.pct_agendados || 0),
              compareValue: showComparison && compareRecalls ? formatPercentage(compareRecalls?.pct_agendados || 0) : undefined,
              subtitle: `${recalls?.agendados || 0}/${recalls?.objetivo_mes || 0}`,
              color: '#e67e22',
              icon: '📅'
            })}
            
            {renderKPICard({
              title: '% Completados',
              value: formatPercentage(recalls?.pct_completados || 0),
              compareValue: showComparison && compareRecalls ? formatPercentage(compareRecalls?.pct_completados || 0) : undefined,
              subtitle: `${recalls?.completados || 0}/${recalls?.objetivo_mes || 0}`,
              color: '#2ecc71',
              icon: '✅'
            })}
          </div>
          
          <div className="chart-container">
            <h3>Estado de Recalls</h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={recallsChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#3498db" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  const renderProduccionDetail = () => {
    if (!dashboardData) {
      return <div className="loading">Cargando datos de producción...</div>;
    }

    const { produccion } = dashboardData;

    return (
      <div className="detail-view">
        <div className="detail-header">
          <button className="back-button" onClick={() => setCurrentView('dashboard')}>
            ← Volver al Dashboard
          </button>
          <h2>👨‍⚕️ Detalle de Producción por Doctores</h2>
        </div>

        <div className="kpi-section">
          <h3>Top 5 Doctores</h3>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={produccion.top_5_doctores || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="doctor" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="produccion" fill="#9b59b6" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="kpi-grid">
            {(Array.isArray(produccion.top_5_doctores) ? produccion.top_5_doctores.slice(0, 3) : []).map((doctor, index) => (
              renderKPICard({
                key: `doctor-${index}`,
                title: doctor.doctor,
                value: formatCurrency(doctor.produccion),
                color: index === 0 ? '#f39c12' : index === 1 ? '#95a5a6' : '#cd7f32',
                icon: index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'
              })
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderFacturacionDetail = () => {
    const [facturacionAnual, setFacturacionAnual] = useState(null);
    const [loading, setLoading] = useState(true);

    React.useEffect(() => {
      const fetchFacturacionAnual = async () => {
        if (!user) return;

        try {
          setLoading(true);
          const url = `${process.env.REACT_APP_BACKEND_URL}/api/facturacion/anual?year=${selectedPeriod.year}&user_param=${user.username}`;
          const response = await fetch(url);
          
          if (!response.ok) {
            throw new Error('Error al obtener datos anuales de facturación');
          }

          const data = await response.json();
          setFacturacionAnual(data);
        } catch (error) {
          console.error('Error fetching facturacion anual:', error);
          showToast('Error al cargar facturación anual', 'error');
        } finally {
          setLoading(false);
        }
      };

      fetchFacturacionAnual();
    }, [selectedPeriod.year, user]);

    if (!dashboardData) {
      return <div className="loading">Cargando datos de facturación...</div>;
    }

    const { facturacion } = dashboardData;
    
    if (!facturacion) {
      return (
        <div className="detail-view">
          <div className="detail-header">
            <button className="back-button" onClick={() => setCurrentView('dashboard')}>
              ← Volver al Dashboard
            </button>
            <h2>💳 Detalle de Facturación</h2>
          </div>
          <div className="empty-state">
            <p>No hay datos de facturación disponibles</p>
          </div>
        </div>
      );
    }

    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];

    return (
      <div className="detail-view">
        <div className="detail-header">
          <button className="back-button" onClick={() => setCurrentView('dashboard')}>
            ← Volver al Dashboard
          </button>
          <h2>💳 Detalle de Facturación</h2>
        </div>

        <div className="detail-grid">
          {/* KPIs Section */}
          <div className="col-span-12">
            <div className="kpi-section">
              <h3>Métricas de Facturación</h3>
              <div className="kpi-grid">
                {renderKPICard({
                  title: 'Facturación Emitida',
                  value: formatCurrency(facturacion.facturacion_emitida),
                  color: '#3498db',
                  icon: '📄'
                })}
                
                {renderKPICard({
                  title: 'Facturación Cobrada',
                  value: formatCurrency(facturacion.facturacion_cobrada),
                  color: '#2ecc71',
                  icon: '💰'
                })}
                
                {renderKPICard({
                  title: 'Objetivo Mensual',
                  value: formatCurrency(facturacion.objetivo_facturacion),
                  color: '#6366f1',
                  icon: '🎯'
                })}
                
                {renderKPICard({
                  title: 'Cumplimiento',
                  value: `${facturacion.cumplimiento_objetivo?.toFixed(1)}%`,
                  color: facturacion.cumplimiento_objetivo >= 100 ? '#2ecc71' : '#f59e0b',
                  icon: '📊'
                })}
              </div>
            </div>
          </div>

          {/* Annual Chart Section */}
          <div className="col-span-12">
            <div className="section-card">
              <h3>🗂️ Facturación Anual {selectedPeriod.year}</h3>
              {loading ? (
                <div className="loading-chart">Cargando datos anuales...</div>
              ) : facturacionAnual?.facturacion_mensual ? (
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height={400}>
                    <ComposedChart 
                      data={facturacionAnual.facturacion_mensual.map(item => ({
                        mes: months[item.mes - 1],
                        facturacion: item.facturacion,
                        objetivo: item.objetivo
                      }))}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="mes" angle={-45} textAnchor="end" height={80} fontSize={11} />
                      <YAxis />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="facturacion" fill="#3498db" name="Facturación" />
                      <Line
                        type="monotone"
                        dataKey="objetivo"
                        stroke="#e74c3c"
                        strokeWidth={3}
                        name="Objetivo"
                        dot={{ fill: '#e74c3c', strokeWidth: 2, r: 4 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="empty-state">
                  <p>No hay datos anuales disponibles para {selectedPeriod.year}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBoxOcupacionDetail = () => {
    if (!dashboardData) {
      return <div className="loading">Cargando datos de ocupación...</div>;
    }

    const { box_ocupacion } = dashboardData;
    
    if (!box_ocupacion) {
      return (
        <div className="detail-view">
          <div className="detail-header">
            <button className="back-button" onClick={() => setCurrentView('dashboard')}>
              ← Volver al Dashboard
            </button>
            <h2>🏢 Detalle de Ocupación de Boxes</h2>
          </div>
          <div className="empty-state">
            <p>No hay datos de ocupación de boxes disponibles</p>
          </div>
        </div>
      );
    }

    return (
      <div className="detail-view">
        <div className="detail-header">
          <button className="back-button" onClick={() => setCurrentView('dashboard')}>
            ← Volver al Dashboard
          </button>
          <h2>🏢 Detalle de Ocupación de Boxes</h2>
        </div>

        <div className="kpi-section">
          <h3>Métricas de Ocupación</h3>
          <div className="kpi-grid">
            {renderKPICard({
              title: 'Ocupación Global',
              value: `${box_ocupacion.ocupacion_global}%`,
              color: box_ocupacion.ocupacion_global >= 75 ? '#2ecc71' : '#e74c3c',
              icon: '🏢'
            })}
            
            {renderKPICard({
              title: 'Boxes sobre Objetivo',
              value: box_ocupacion.boxes_sobre_objetivo,
              subtitle: `de ${box_ocupacion.total_boxes} total`,
              color: '#2ecc71',
              icon: '✅'
            })}
            
            {renderKPICard({
              title: 'Boxes bajo Objetivo',
              value: box_ocupacion.boxes_bajo_objetivo,
              subtitle: `de ${box_ocupacion.total_boxes} total`,
              color: '#e74c3c',
              icon: '⚠️'
            })}
          </div>
          
          <div className="chart-container">
            <h3>Ocupación por Box</h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={box_ocupacion.ocupacion_por_box ? Object.entries(box_ocupacion.ocupacion_por_box).map(([boxId, data]) => ({
                box: boxId,
                ocupacion: data.ocupacion,
                objetivo: data.objetivo
              })) : []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="box" />
                <YAxis />
                <Tooltip formatter={(value) => `${value}%`} />
                <Bar dataKey="ocupacion" fill="#3498db" name="Ocupación %" />
                <Bar dataKey="objetivo" fill="#e74c3c" name="Objetivo %" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  if (!user) {
    console.log('RENDER: No user found, showing login screen');
    return renderLoginScreen();
  }

  console.log('RENDER: User found, showing dashboard:', user);
  return (
    <div className="app" key={renderKey}>
      {renderHeader()}
      {renderNavigation()}
      <main className="main-content">
        {renderContent()}
      </main>
      {renderToast()}
    </div>
  );
};

export default App;
